import { Loader2, Search } from "lucide-react";
import type { JdFieldRow, JdFieldsResult } from "../../hooks/queries";

export interface JdSelection {
  shown: JdFieldRow[];
  selectableAll: JdFieldRow[];
  selectableShown: JdFieldRow[];
  allPicked: boolean;
  allShownPicked: boolean;
  pickedAlreadyIn: JdFieldRow[];
}

type PickedSetter = (
  next: Set<string> | ((cur: Set<string>) => Set<string>),
) => void;

function emptyJdCopy(
  fields: JdFieldRow[] | undefined,
  jd: JdFieldsResult | undefined,
  status: { configured?: boolean; connected?: boolean } | undefined,
): string {
  if (fields?.length) return "No fields match.";
  if (status && !status.configured)
    return "John Deere isn’t set up on this deployment yet.";
  if (status && !status.connected)
    return "Your account isn’t connected to John Deere — connect it from the profile menu, then reopen this.";
  if (jd && jd.orgCount === 0)
    return "No John Deere organizations are visible to your account.";
  if (jd && jd.failedOrgs > 0)
    return `Couldn’t read fields from ${jd.failedOrgs} of your John Deere organization${jd.failedOrgs === 1 ? "" : "s"}.`;
  return "Your John Deere organizations returned no fields.";
}

function toggleSet(
  setPicked: PickedSetter,
  rows: JdFieldRow[],
  fullyPicked: boolean,
) {
  setPicked((cur) => {
    const next = new Set(cur);
    for (const f of rows)
      if (fullyPicked) next.delete(f.id);
      else next.add(f.id);
    return next;
  });
}

function JdFieldList({
  shown,
  picked,
  setPicked,
}: {
  shown: JdFieldRow[];
  picked: Set<string>;
  setPicked: PickedSetter;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col rounded border border-line">
      <div className="flex items-center gap-2 border-b border-line px-1.5 py-1 text-[9px] uppercase tracking-wide text-faint">
        <span className="w-[13px] shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">Field</span>
        <span className="w-24 shrink-0 sm:w-32">Organization</span>
      </div>
      <ul className="min-h-0 flex-1 overflow-auto p-1">
        {shown.map((f) => {
          const org = f.orgName ?? f.jdOrgId;
          return (
            <li key={f.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-hover">
                <input
                  type="checkbox"
                  checked={picked.has(f.id)}
                  onChange={() =>
                    setPicked((cur) => {
                      const n = new Set(cur);
                      if (n.has(f.id)) n.delete(f.id);
                      else n.add(f.id);
                      return n;
                    })
                  }
                  aria-label={
                    org ? `${f.name ?? f.id} (${org})` : (f.name ?? f.id)
                  }
                />
                <span className="min-w-0 flex-1 truncate">
                  {f.name ?? f.id}
                </span>
                {f.linkedFieldId && (
                  <span
                    className="shrink-0 rounded bg-amber-500/15 px-1 py-0.5 text-[9px] text-amber-500"
                    title={`Already in your account as “${f.linkedFieldName ?? "an existing field"}”`}
                  >
                    already in
                    {f.linkedFieldName ? ` — ${f.linkedFieldName}` : ""}
                  </span>
                )}
                <span
                  className="w-24 shrink-0 truncate text-faint sm:w-32"
                  title={org || undefined}
                >
                  {org || "—"}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function JdSearch({
  query,
  setQuery,
}: {
  query: string;
  setQuery: (next: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 rounded border border-line px-1.5">
      <Search size={11} className="text-faint" />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search fields or organizations…"
        className="w-full bg-transparent py-1 text-[11px] outline-none placeholder:text-faint"
      />
    </div>
  );
}

function JdSelectButtons({
  sel,
  setPicked,
}: {
  sel: JdSelection;
  setPicked: (next: Set<string> | ((cur: Set<string>) => Set<string>)) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        className="rounded border border-line px-2 py-1 text-[11px] text-muted hover:bg-hover hover:text-fg disabled:opacity-40"
        onClick={() => toggleSet(setPicked, sel.selectableAll, sel.allPicked)}
        disabled={sel.selectableAll.length === 0}
      >
        {sel.allPicked
          ? "Clear all"
          : `Select all (${sel.selectableAll.length})`}
      </button>
      <button
        className="rounded border border-line px-2 py-1 text-[11px] text-muted hover:bg-hover hover:text-fg disabled:opacity-40"
        onClick={() =>
          toggleSet(setPicked, sel.selectableShown, sel.allShownPicked)
        }
        disabled={sel.selectableShown.length === 0}
        title="Only the fields left on screen by the search above"
      >
        {sel.allShownPicked
          ? "Clear visible"
          : `Select all visible (${sel.selectableShown.length})`}
      </button>
    </div>
  );
}

function JdListState({
  isLoading,
  isError,
  error,
  fields,
  jd,
  status,
  shown,
  picked,
  setPicked,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  fields: JdFieldRow[] | undefined;
  jd: JdFieldsResult | undefined;
  status: { configured?: boolean; connected?: boolean } | undefined;
  shown: JdFieldRow[];
  picked: Set<string>;
  setPicked: (next: Set<string> | ((cur: Set<string>) => Set<string>)) => void;
}) {
  if (isLoading) {
    return (
      <p className="flex items-center gap-1.5 py-4 text-[11px] text-muted">
        <Loader2 size={12} className="animate-spin" /> Loading your John Deere
        fields…
      </p>
    );
  }
  if (isError) {
    return (
      <p className="py-4 text-[11px] text-red-400">
        {error instanceof Error && error.message
          ? error.message
          : "Couldn’t reach John Deere Ops."}{" "}
        You can reconnect from the profile menu, then reopen this.
      </p>
    );
  }
  if (shown.length === 0) {
    return (
      <p className="py-4 text-[11px] text-faint">
        {emptyJdCopy(fields, jd, status)}
      </p>
    );
  }
  return <JdFieldList shown={shown} picked={picked} setPicked={setPicked} />;
}

function JdDuplicateNote({ rows }: { rows: JdFieldRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div
      role="status"
      className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-fg"
    >
      {rows.length === 1
        ? `“${rows[0].name}” is already in your account${rows[0].linkedFieldName ? ` as “${rows[0].linkedFieldName}”` : ""} — bringing it in again would duplicate it.`
        : `${rows.length} of the selected fields are already in your account — bringing them in again would duplicate them.`}
    </div>
  );
}

function JdSendError({
  err,
  sent,
}: {
  err: string | null;
  sent: string[] | null;
}) {
  if (!err) return null;
  return (
    <div className="rounded border border-red-500/40 bg-red-500/10 px-2 py-1.5">
      <p className="text-[11px] text-red-400">{err}</p>
      {sent && sent.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-[10px] text-faint">
            What we sent to John Deere
          </summary>
          <ul className="mt-1 space-y-0.5 text-[10px] text-faint">
            {sent.map((line) => (
              <li key={line} className="break-all font-mono">
                {line}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function JdCommit({
  pickedCount,
  busy,
  onBringIn,
}: {
  pickedCount: number;
  busy: boolean;
  onBringIn: () => void;
}) {
  return (
    <>
      <div className="flex items-center justify-between gap-2 border-t border-line pt-2">
        <span className="text-[11px] text-muted">{pickedCount} selected</span>
        <button
          className="flex items-center gap-1.5 rounded bg-accent px-2 py-1.5 text-xs font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
          disabled={pickedCount === 0 || busy}
          onClick={onBringIn}
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          {busy
            ? "Fetching boundaries…"
            : `Bring in ${pickedCount || ""} field${pickedCount === 1 ? "" : "s"}`}
        </button>
      </div>
      <p className="text-[10px] text-faint">
        Then: boundaries are checked against each other and your existing fields
        — duplicates are skipped and recorded, a field already in your account
        is linked rather than duplicated, and all John Deere data is pulled for
        every field brought in.
      </p>
      {pickedCount > 0 && (
        <p className="text-[10px] text-faint">
          Bringing in {pickedCount} field{pickedCount === 1 ? "" : "s"} starts
          each one’s soil-data build — a few hours for a handful, up to a day
          for dozens. You can keep working while they fill in.
        </p>
      )}
    </>
  );
}

export function JdForm({
  query,
  setQuery,
  sel,
  setPicked,
  isLoading,
  isError,
  error,
  fields,
  jd,
  status,
  picked,
  err,
  sent,
  busy,
  onBringIn,
}: {
  query: string;
  setQuery: (next: string) => void;
  sel: JdSelection;
  setPicked: (next: Set<string> | ((cur: Set<string>) => Set<string>)) => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  fields: JdFieldRow[] | undefined;
  jd: JdFieldsResult | undefined;
  status: { configured?: boolean; connected?: boolean } | undefined;
  picked: Set<string>;
  err: string | null;
  sent: string[] | null;
  busy: boolean;
  onBringIn: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <JdSearch query={query} setQuery={setQuery} />
      <JdSelectButtons sel={sel} setPicked={setPicked} />
      <JdListState
        isLoading={isLoading}
        isError={isError}
        error={error}
        fields={fields}
        jd={jd}
        status={status}
        shown={sel.shown}
        picked={picked}
        setPicked={setPicked}
      />
      <JdDuplicateNote rows={sel.pickedAlreadyIn} />
      <JdSendError err={err} sent={sent} />
      <JdCommit pickedCount={picked.size} busy={busy} onBringIn={onBringIn} />
    </div>
  );
}
